// Report page grid and widget footprints, ported from the June 2026 summer builder
// (vendor/compliance-dashboard/utils/widgetGrid.js + tokens/dashboardTokens.js).
//
// 24 fluid columns and a fixed 28px row pitch (12px row + 16px gutter), inside a
// 16px page margin. Column width flexes with the page format; the row pitch does not.
// Footprints are in cells { c, r }: c = columns (1–24), r = 28px rows.
// Row → height: h = r × 28 − 16 (r2 = 40px, r4 = 96, r6 = 152, r12 = 320).
//
// Keys are Cowork widget types (DatoxComplianceDashboard WIDGETS). The agent cheat
// sheet (.input/AGENT_LAYOUT_CHEATSHEET.md) is derived from these numbers.

export const GRID_COLS = 24
export const GRID_GUTTER = 16
export const GRID_MARGIN = 16
export const GRID_ROW = 12
export const GRID_ROW_PITCH = GRID_ROW + GRID_GUTTER // 28

export const CANVAS_PRESETS = {
  portrait: { orientation: "portrait", width: 794, height: 1123 },
  landscape: { orientation: "landscape", width: 1123, height: 794 },
}

// Default footprint: the size a new widget is placed at.
export const WIDGET_CELLS = {
  header: { c: 24, r: 4 },
  title: { c: 24, r: 2 },
  text: { c: 12, r: 6 },
  image: { c: 8, r: 8 },
  kpi: { c: 8, r: 6 },
  table: { c: 24, r: 12 },
  freeTable: { c: 24, r: 12 },
  bar: { c: 12, r: 12 },
  line: { c: 12, r: 12 },
  multiSeries: { c: 12, r: 12 },
  scatter: { c: 12, r: 12 },
  pie: { c: 12, r: 12 },
  treemap: { c: 12, r: 12 },
  funnel: { c: 12, r: 12 },
  heatmap: { c: 24, r: 16 },
  filter: { c: 6, r: 4 },
}

// Minimum footprint: resizing and load-time normalisation never go below it.
// Deliberately small so dense pages (4 charts or 6 KPIs per row) still fit.
export const WIDGET_MIN_CELLS = {
  header: { c: 16, r: 2 },
  title: { c: 8, r: 2 },
  text: { c: 6, r: 4 },
  image: { c: 4, r: 4 },
  kpi: { c: 2, r: 4 },
  table: { c: 8, r: 6 },
  freeTable: { c: 8, r: 6 },
  bar: { c: 6, r: 6 },
  line: { c: 6, r: 6 },
  multiSeries: { c: 6, r: 6 },
  scatter: { c: 6, r: 6 },
  pie: { c: 6, r: 6 },
  treemap: { c: 6, r: 6 },
  funnel: { c: 6, r: 6 },
  heatmap: { c: 12, r: 10 },
  filter: { c: 4, r: 2 },
}

export const DEFAULT_WIDGET_CELLS = { c: 12, r: 10 }
export const DEFAULT_WIDGET_MIN_CELLS = { c: 6, r: 6 }

// A KPI with a target renders at least 180px tall (dashboardWidgetHeight): 7 rows.
export const KPI_TARGET_MIN_ROWS = 7

// Header band (display): a page-top header renders as a full-bleed band whose bottom edge
// sits on this row line, so every report has the same band: 84px landscape, 112px portrait.
export const HEADER_ROWS = { landscape: 3, portrait: 4 }

// Last-resort floors for fitting an overflowing page (display only): below the type minimum,
// but the widget still reads (a KPI keeps its label and value, a table its header and a row).
export const FIT_FLOOR_ROWS = {
  header: 2,
  title: 2,
  text: 2,
  image: 3,
  kpi: 3,
  table: 5,
  freeTable: 5,
  filter: 2,
}
export const DEFAULT_FIT_FLOOR_ROWS = 5

// Widget types whose display height follows their measured content (tables grow into free
// page space below them; text shrinks to its content).
export const CONTENT_FIT_TYPES = ["table", "freeTable", "text"]
export const CONTENT_GROW_TYPES = ["table", "freeTable"]
// Charts have no measured content: their natural height is the type's default footprint
// (WIDGET_CELLS, summer), and a chart saved shorter grows toward it while the page has free rows.
export const FOOTPRINT_GROW_TYPES = ["bar", "line", "multiSeries", "scatter", "pie", "treemap", "funnel", "heatmap"]

export function widgetCells(type) {
  return WIDGET_CELLS[type] || DEFAULT_WIDGET_CELLS
}

export function widgetMinCells(type, widget) {
  const min = WIDGET_MIN_CELLS[type] || DEFAULT_WIDGET_MIN_CELLS
  return type === "kpi" && (widget?.style?.target || widget?.kpi_display?.target) ? { c: min.c, r: Math.max(min.r, KPI_TARGET_MIN_ROWS) } : min
}
